import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import {
  EXPLICIT_EVENT_KINDS,
  blankTimelineEventDraft,
  draftFromTimelineEvent,
  validateTimelineEventDraft,
} from '../../features/timeline/timelineModel';
import type {
  TimelineEvent,
  TimelineEventDraft,
} from '../../features/timeline/types';
import type { GoalSummary } from '../../features/goals/types';
import type { WorkstreamSummary } from '../../features/workstreams/types';
import type { ServiceSummary } from '../../features/services/types';
import type { TeamDocumentSummary } from '../../features/documents/types';
import type { DecisionSummary } from '../../features/decisions/types';
import type { Task } from '../../features/tasks/types';

export interface TimelineEventModalProps {
  open: boolean;
  onClose: () => void;
  defaultEngineer: string;
  initialEvent?: TimelineEvent;
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  services: ServiceSummary[];
  documents: TeamDocumentSummary[];
  decisions: DecisionSummary[];
  tasks: Task[];
  onSubmit: (draft: TimelineEventDraft) => Promise<unknown>;
}

const KIND_OPTIONS: SelectOption[] = EXPLICIT_EVENT_KINDS.map((k) => ({
  value: k.value,
  label: k.label,
}));

/** Add / edit an explicit timeline event (a manual milestone or note). */
export function TimelineEventModal({
  open,
  onClose,
  defaultEngineer,
  initialEvent,
  goals,
  workstreams,
  services,
  documents,
  decisions,
  tasks,
  onSubmit,
}: TimelineEventModalProps) {
  const [draft, setDraft] = useState<TimelineEventDraft>(blankTimelineEventDraft());
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(
      initialEvent
        ? draftFromTimelineEvent(initialEvent)
        : blankTimelineEventDraft({ engineer: defaultEngineer || undefined }),
    );
    setProblems([]);
  }, [open, initialEvent, defaultEngineer]);

  const set = <K extends keyof TimelineEventDraft>(key: K, value: TimelineEventDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const goalOptions: SelectOption[] = goals.map((g) => ({ value: g.id, label: g.title || g.id }));
  const workstreamOptions: SelectOption[] = workstreams.map((w) => ({ value: w.id, label: w.title || w.id }));
  const serviceOptions: SelectOption[] = services.map((s) => ({ value: s.id, label: s.name || s.id }));
  const docOptions: SelectOption[] = documents.map((d) => ({ value: d.id, label: d.title || d.id }));
  const decisionOptions: SelectOption[] = decisions.map((d) => ({ value: d.id, label: d.decision || d.id }));
  const storyOptions: SelectOption[] = tasks.map((t) => ({ value: t.id, label: t.title || t.id }));

  const submit = async () => {
    const next: TimelineEventDraft = {
      ...draft,
      title: draft.title.trim(),
    };
    const found = validateTimelineEventDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to save event.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="lg"
      title={initialEvent ? 'Edit event' : 'Add event'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            {initialEvent ? 'Save changes' : 'Add event'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        <Input
          label="Title (required)"
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
        />
        <Textarea
          label="Detail"
          rows={2}
          value={draft.description ?? ''}
          onChange={(e) => set('description', e.target.value || undefined)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            type="date"
            label="Date (required)"
            value={draft.date}
            onChange={(e) => set('date', e.target.value)}
          />
          <Select
            label="Kind"
            options={KIND_OPTIONS}
            value={draft.kind}
            onChange={(e) => set('kind', e.target.value as TimelineEventDraft['kind'])}
          />
          <Input
            label="Engineer"
            placeholder="Optional"
            value={draft.engineer ?? ''}
            onChange={(e) => set('engineer', e.target.value || undefined)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="Related goal" placeholder="None" value={draft.relatedGoalId ?? ''} options={goalOptions} onChange={(e) => set('relatedGoalId', e.target.value || undefined)} />
          <Select label="Related workstream" placeholder="None" value={draft.relatedWorkstreamId ?? ''} options={workstreamOptions} onChange={(e) => set('relatedWorkstreamId', e.target.value || undefined)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="Related service" placeholder="None" value={draft.relatedServiceId ?? ''} options={serviceOptions} onChange={(e) => set('relatedServiceId', e.target.value || undefined)} />
          <Select label="Related document" placeholder="None" value={draft.relatedDocId ?? ''} options={docOptions} onChange={(e) => set('relatedDocId', e.target.value || undefined)} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label="Related decision" placeholder="None" value={draft.relatedDecisionId ?? ''} options={decisionOptions} onChange={(e) => set('relatedDecisionId', e.target.value || undefined)} />
          <Select label="Related story" placeholder="None" value={draft.relatedStoryId ?? ''} options={storyOptions} onChange={(e) => set('relatedStoryId', e.target.value || undefined)} />
        </div>
      </div>
    </Modal>
  );
}

export default TimelineEventModal;
